import AppsIcon from "@mui/icons-material/Apps";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import LogoutSharpIcon from "@mui/icons-material/LogoutSharp";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SettingsSharpIcon from "@mui/icons-material/SettingsSharp";
import SmartDisplayOutlinedIcon from "@mui/icons-material/SmartDisplayOutlined";
import WidgetsIcon from "@mui/icons-material/Widgets";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import VideocamIcon from "@mui/icons-material/Videocam";
import TheatersOutlinedIcon from "@mui/icons-material/TheatersOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import { Box, Typography } from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Stack from "@mui/material/Stack";
import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { DISABLED_ROUTES } from "../config/disabled-routes";
import { BILLING_ENABLED, GUIDED_TOUR_ENABLED } from "../config/features";
import { useMainApi } from "../provider/main-api.provider";
import { COLOR } from "../theme/theme.config";
import CreditsPanel from "./credits-panel.component";
import { formatDate } from "../utils/date";
import GuidedTour from "./guidedTour";
import UserProfileBlock from "./user-profile-block.component";

const mainListItems = [
  {
    text: "Dashboard",
    icon: (
      <AppsIcon
        sx={{
          color: COLOR.iconColor,
        }}
      />
    ),
    route: "/",
    id: "nav-dashboard",
  },
  {
    text: "Create Video",
    icon: (
      <VideoCallIcon
        sx={{
          color: COLOR.iconColor,
        }}
      />
    ),
    route: "/video-create",
    id: "nav-video-create-studio",
  },
  {
    text: "Quick Video",
    icon: (
      <BoltOutlinedIcon
        sx={{
          color: COLOR.iconColor,
        }}
      />
    ),
    route: "/quick-video",
    id: "nav-quick-video",
  },
  {
    text: "New Video",
    icon: (
      <VideoCallIcon
        sx={{
          color: COLOR.iconColor,
        }}
      />
    ),
    route: "/create-video",
    id: "nav-create-video",
  },
  {
    text: "New Template",
    icon: (
      <DashboardCustomizeIcon
        sx={{
          color: COLOR.iconColor,
        }}
      />
    ),
    route: "/create-template",
    id: "nav-create-template",
  },
  {
    text: "Video Studio",
    icon: (
      <SmartDisplayOutlinedIcon
        sx={{
          color: COLOR.iconColor,
        }}
      />
    ),
    route: "/video-configuration",
    id: "nav-video-studio",
  },
  {
    text: "Scripts",
    icon: (
      <AutoAwesomeIcon
        sx={{
          color: COLOR.iconColor,
        }}
      />
    ),
    route: "/content-engine",
    id: "nav-content-engine-v2",
  },
  {
    text: "Projects",
    icon: (
      <VideocamIcon
        sx={{
          color: COLOR.iconColor,
        }}
      />
    ),
    route: "/videos",
    id: "nav-all-videos",
  },
  {
    text: "Video Projects",
    icon: (
      <TheatersOutlinedIcon
        sx={{
          color: COLOR.iconColor,
        }}
      />
    ),
    route: "/video-projects",
    id: "nav-video-projects",
  },
  {
    text: "Library",
    icon: (
      <MenuBookOutlinedIcon
        sx={{
          color: COLOR.iconColor,
        }}
      />
    ),
    route: "/library",
    id: "nav-library",
  },
  {
    text: "Templates",
    icon: (
      <WidgetsIcon
        sx={{
          color: COLOR.iconColor,
        }}
      />
    ),
    route: "/templates",
    id: "nav-all-templates",
  },
];

const hiddenRoutes = new Set<string>(DISABLED_ROUTES);
const visibleMainListItems = mainListItems.filter((item) => !hiddenRoutes.has(item.route));

const secondaryListItems = [
  {
    text: "Settings",
    icon: (
      <SettingsSharpIcon
        sx={{
          color: COLOR.iconColor,
        }}
      />
    ),
    route: "/settings",
    id: "nav-settings",
  },
  {
    text: "Logout",
    icon: (
      <LogoutSharpIcon
        sx={{
          color: COLOR.iconColor,
        }}
      />
    ),
    route: "/logout",
    id: "nav-logout",
  },
];

export default function MenuContent() {
  const navigate = useNavigate();
  const { credits, me } = useMainApi();

  const [freeCreditBalance, setFreeCreditBalance] = useState(0);
  const [totalFreeCredits, setTotalFreeCredits] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    setTotalFreeCredits(credits?.combined.total || 0);
    setFreeCreditBalance(credits?.combined.balance || 0);
    setName([me?.first_name, me?.last_name].filter(Boolean).join(" "));
    setEmail(me?.email ?? "");
  }, [credits, me]);

  return (
    <Stack
      sx={{
        marginTop: "20px",
        flexGrow: 1,
        p: 1,
        justifyContent: "space-between",
      }}
    >
      <List dense>
        {visibleMainListItems.map((item, index) => (
          <ListItem
            className={"menu-link"}
            key={index}
            disablePadding
            sx={{ display: "block", marginBottom: "10px" }}
          >
            <ListItemButton sx={{ p: 0 }}>
              <NavLink
                id={item.id}
                style={{
                  color: COLOR.primary,
                  textDecoration: "none",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 16px",
                }}
                to={item.route}
                className="menu-item d-flex align-items-center"
              >
                <ListItemIcon className={"menu-icon"}>{item.icon}</ListItemIcon>
                {item.text}
              </NavLink>
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <List dense>
        {GUIDED_TOUR_ENABLED && <GuidedTour />}

        <Box
          sx={{
            display: "flex",
            justifyContent: "left",
            pl: 2.25,
            pr: 2.25,
            marginBottom: 2,
            gap: 1,
          }}
        >
          {/*
          <Button
            startIcon={
              <Box
                sx={{
                  minWidth: 20,
                  minHeight: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EmojiNatureIcon sx={{ fontSize: 20 }} />
              </Box>
            }
            variant="contained"
            sx={{
              width: "100%",
              background: "linear-gradient(90deg, #00e09e 0%, #00b4d8 100%)",
              color: "#09091c",
              // color: "#5d8df5",
              "&:hover": {
                background: "linear-gradient(90deg, #00b4d8 0%, #00e09e 100%)",
                color: "#09091c",
                boxShadow: "0 4px 16px rgba(0,0,0,0.13)",
              },
              fontWeight: "bold",
              fontFamily: "system-ui",
              textTransform: "capitalize",
              fontSize: { xs: "12px" },
              borderRadius: "4px",
            }}
            onClick={() => {
              window.open("https://theunplan.featurebase.app/", "_blank");
            }}
          >
            Feedback
          </Button>
          */}
        </Box>

        {/*
        <Box
          sx={{
            display: "flex",
            justifyContent: "left",
            pl: 2.25,
            pr: 2.25,
            marginBottom: 1,
          }}
        >
          <SubscriptionStatus />
        </Box>
        */}

        {BILLING_ENABLED && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "left",
              pl: 2.25,
              pr: 2.25,
              marginBottom: 3,
              cursor: "pointer",
            }}
            onClick={() => navigate("/settings/subscription")}
          >
            <CreditsPanel
              freeCreditBalance={freeCreditBalance}
              totalFreeCredits={totalFreeCredits}
            />
          </Box>
        )}

        <Box
          component="hr"
          sx={{
            border: 0,
            height: "1px",
            backgroundColor: "#999DB3",
            margin: "8px 0",
          }}
        />

        {secondaryListItems.map((item, index) => (
          <ListItem
            className="menu-link"
            key={index}
            disablePadding
            sx={{ display: "block", marginTop: "10px" }}
          >
            <ListItemButton sx={{ p: 0 }}>
              <NavLink
                style={{
                  color: COLOR.primary,
                  textDecoration: "none",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 16px",
                }}
                to={item.route}
                className="menu-item d-flex align-items-center"
              >
                <ListItemIcon className={"menu-icon"}>{item.icon}</ListItemIcon>
                {item.text}
              </NavLink>
            </ListItemButton>
          </ListItem>
        ))}
        <Box sx={{ px: 2.25, py: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#999DB3" }}>
            <ScheduleIcon sx={{ fontSize: 12 }} />
            <Typography sx={{ fontSize: 11 }}>
              Last login: {me?.last_login ? formatDate(me.last_login, true) : "Just now (demo)"}
            </Typography>
          </Box>
        </Box>
        <UserProfileBlock
          name={name}
          email={email}
          avatarUrl={me?.profile?.profile_picture ?? undefined}
        />
      </List>
    </Stack>
  );
}
